import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  NgZone,
  OnDestroy,
  effect,
  inject,
  signal
} from '@angular/core';
import { PieChart } from '@app/core/shared/models/charts';
import ChartUtility from '../utilities/chart.utility';

@Component({
  selector: 'app-field-view-chart-pie',
  templateUrl: './field-view-chart-pie.component.html',
  styleUrls: ['./field-view-chart-pie.component.scss'],
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FieldViewChartPieComponent implements AfterViewInit, OnDestroy {
  private root: am5.Root;
  private series: am5percent.PieSeries;
  private legend: am5.Legend;

  @Input({ required: true }) chartTitle: string;
  @Input({ required: true }) chartId: string;
  @Input({ required: true }) dataIsLoading: boolean;

  private readonly chartDataSignal = signal<PieChart>({ pieChartData: [], totalCount: 0 });
  private pieChartLabel: am5.Label;

  private readonly ngZone = inject(NgZone);

  constructor() {
    effect(() => {
      const data = this.chartDataSignal();

      if (this.series && !this.dataIsLoading) {
        this.updatePieChartData(data);
      }

      if (this.series && this.dataIsLoading) {
        this.series.data.clear();
        this.setPieChartLabel(this.series.chart, 0);
        this.legend.data.setAll([]);
      }
    });
  }

  // Angular v17 we should be able to replace private chartDataSignal and this @Input with:
  // chartData = input.required<PieChart>();

  @Input({ required: true })
  set chartData(value: PieChart) {
    this.chartDataSignal.set(value);
  }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      am5.addLicense('AM5C298823021');
      this.root = am5.Root.new(this.chartId);
      this.createPieChart();

      if (!this.dataIsLoading) {
        this.updatePieChartData(this.chartDataSignal());
      }
    });
  }

  private setPieChartLabel(chart: am5percent.PieChart, totalCount: number) {
    if (!this.pieChartLabel) {
      this.pieChartLabel = am5.Label.new(this.root, {
        text: totalCount.toString(),
        fontSize: 20,
        fill: am5.color('#fff'),
        x: am5.percent(50),
        centerX: am5.percent(50),
        y: am5.percent(50),
        centerY: am5.percent(50),
        paddingTop: 0,
        paddingBottom: 0
      });
      chart.children.unshift(this.pieChartLabel);
    } else {
      this.pieChartLabel.set('text', totalCount.toString());
    }
  }

  private createPieChart() {
    const chart = this.root.container.children.push(
      am5percent.PieChart.new(this.root, {
        width: am5.percent(80),
        height: am5.p100,
        innerRadius: am5.p50,
        x: am5.percent(0),
        centerX: am5.percent(0)
      })
    );

    ChartUtility.setChartTitle(this.root, chart, this.chartTitle);

    this.legend = ChartUtility.createLegend(this.root, chart, 'name');

    this.series = chart.series.push(
      am5percent.PieSeries.new(this.root, {
        valueField: 'count',
        categoryField: 'name',
        radius: am5.percent(50),
        legendLabelText: '{category}',
        legendValueText: '{value}'
      })
    );

    this.series.slices.template.adapters.add('fill', (_, target) => {
      const dataItem = target.dataItem?.dataContext as { colour?: string };
      return am5.color(dataItem?.colour || '#cccccc');
    });

    this.series.labels.template.setAll({ visible: false });
    this.series.ticks.template.setAll({ visible: false });

    this.series.slices.template.set(
      'tooltipText',
      `{name}: {valuePercentTotal.formatNumber('0.0p')} ({value})`
    );
  }

  private updatePieChartData(data: PieChart) {
    if (!data) return;
    this.series.data.clear();
    this.series.data.setAll(data.pieChartData);

    const chart = this.series.chart;
    this.setPieChartLabel(chart, data.totalCount);

    this.legend?.data.setAll(this.series.dataItems);
  }

  ngOnDestroy() {
    if (this.root) {
      this.root.dispose();
    }
  }
}
